import { useCallback, memo } from 'react';
import { useIntl } from 'react-intl';
import { useReactiveVar } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';

import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';
import { isLoggedInVar } from '@ocx-app/modules/auth/authReactiveVariables';
import { clearCache } from '@ocx-app/modules/cache-layer/utils';
import { useNavigateAfterSignIn } from '@ocx-app/modules/auth/use-navigate-after-sign-in.hook';

import { logInDev } from '@ocx/utils';
import { Loading } from '@ocx/ui';

import { SignupWithLinkLayout } from './signup-with-link.layout';
import { useSignupWithLinkController } from './use-signup-with-link-controller.hook';
import { messages } from '../../signup.messages';

export const SignupWithLinkController = memo(() => {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const { pushRequestLink, pushToHome } = useGetNavigationActions();
  const navigateAfterSignIn = useNavigateAfterSignIn();

  const { token = '' } = useParams<{ token: string }>();
  const isLoggedIn = useReactiveVar(isLoggedInVar);

  const onSignupCompleted = useCallback(() => {
    const navigateToHome = () => {
      snackbar.open(intl.formatMessage(messages['signUp:successfullyLoggedInToastMessage']), {
        variant: 'success',
      });
      navigateAfterSignIn();
    };
    clearCache().catch(logInDev).finally(navigateToHome);
  }, [snackbar, intl, navigateAfterSignIn]);

  const onSignupFailed = useCallback(() => {
    if (isLoggedIn) {
      pushToHome();
      return;
    }

    snackbar.open(intl.formatMessage(messages['signUp:expiredLinkMessage']), {
      variant: 'error',
    });
    pushRequestLink();
  }, [isLoggedIn, snackbar, intl, pushRequestLink, pushToHome]);

  useSignupWithLinkController(token, {
    onCompleted: onSignupCompleted,
    onError: onSignupFailed,
  });

  return (
    <SignupWithLinkLayout>
      <Box textAlign="center" margin="auto">
        <Loading />
      </Box>
    </SignupWithLinkLayout>
  );
});
